import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiHost } from '../utils/apiHost';
import { MAKS_TINGKAT, periksaTingkat, hargaGrosirUntuk, ringkasGrosir } from '../utils/hargaGrosir';

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const rp = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;

/**
 * Atur harga grosir bertingkat satu produk di satu cabang (tombol "Grosir" di halaman Produk).
 * Harga grosir berlaku untuk SEMUA unit begitu jumlah di keranjang mencapai batas. Aturan: utils/hargaGrosir.js (sama dengan server).
 * product = { baranguuid, namabarang, harga, cabanguuid, hargaGrosir }.
 */
const HargaGrosirDialog = ({ product, superadmin, onClose, onSaved }) => {
  const [baris, setBaris] = useState([]);
  const [proses, setProses] = useState(false);
  const [galat, setGalat] = useState('');

  useEffect(() => {
    if (!product) return;
    const awal = (product.hargaGrosir || []).map((t) => ({ min: String(t.min), harga: String(t.harga) }));
    setBaris(awal);
    setGalat('');
  }, [product]);

  if (!product) return null;
  const dasar = Number(product.harga) || 0;
  const hasil = periksaTingkat(baris, dasar);
  const contoh = hasil.tingkat && hasil.tingkat.length > 0 ? hasil.tingkat[hasil.tingkat.length - 1].min + 1 : null;

  const ubah = (i, patch) => setBaris((b) => b.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const simpan = async () => {
    setProses(true);
    setGalat('');
    try {
      await axios.put(
        `${base()}/barangcabang/${product.baranguuid}/grosir`,
        { tingkat: hasil.tingkat || [], ...(superadmin ? { cabangUuid: product.cabanguuid } : {}) },
        { withCredentials: true }
      );
      onSaved?.();
      onClose();
    } catch (e) {
      setGalat(e?.response?.data?.message || e?.message || 'Gagal menyimpan');
      setProses(false);
    }
  };

  return (
    <Dialog open onClose={proses ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Harga grosir • {product.namabarang}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Harga biasa <b>{rp(dasar)}</b>. Begitu jumlah di keranjang mencapai batas, <b>semua unit</b> memakai harga grosir.
          {superadmin && product.Cabang?.namacabang ? ` Cabang: ${product.Cabang.namacabang}.` : ''}
        </Typography>
        {baris.map((b, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField size="small" type="number" label="Mulai jumlah" value={b.min} onChange={(e) => ubah(i, { min: e.target.value })} inputProps={{ min: 2 }} sx={{ flex: 1 }} />
            <TextField size="small" type="number" label="Harga satuan" value={b.harga} onChange={(e) => ubah(i, { harga: e.target.value })} inputProps={{ min: 0 }} sx={{ flex: 1.4 }} />
            <IconButton size="small" aria-label="Hapus tingkat" onClick={() => setBaris((x) => x.filter((_, idx) => idx !== i))}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        ))}
        {baris.length < MAKS_TINGKAT && (
          <Button size="small" onClick={() => setBaris((x) => [...x, { min: '', harga: '' }])}>+ Tambah tingkat</Button>
        )}
        {baris.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Belum ada tingkat: produk dijual dengan harga biasa.</Typography>}
        {hasil.galat && baris.length > 0 && <Alert severity="error" sx={{ mt: 1.5 }}>{hasil.galat}</Alert>}
        {hasil.tingkat && hasil.tingkat.length > 0 && (
          <Box sx={{ mt: 1.5, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Contoh</Typography>
            {[1, ...hasil.tingkat.map((t) => t.min), contoh].map((n, i) => {
              const h = hargaGrosirUntuk(dasar, hasil.tingkat, n);
              return <Typography key={`${n}-${i}`} variant="body2">Beli {n}: {n} × {rp(h)} = <b>{rp(n * h)}</b>{h < dasar ? ` (hemat ${rp(n * (dasar - h))})` : ''}</Typography>;
            })}
          </Box>
        )}
        {galat && <Alert severity="error" sx={{ mt: 1.5 }}>{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Batal</Button>
        <Button variant="contained" onClick={simpan} disabled={proses || !!hasil.galat}>{proses ? 'Menyimpan…' : 'Simpan'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export { ringkasGrosir };
export default HargaGrosirDialog;
